import { useMsal } from "@azure/msal-react";
import { AccountInfo } from "@azure/msal-browser";
import { msalConfig } from "../config/msalConfig";
import { useState } from "react";
import { UserPartial } from "../models/user.model";
import { useLocation } from "react-router-dom";
import { USER_AUTH_TYPE } from "../enums/user-auth-type.enum";

type AuthData = {
  idTokenClaims: UserPartial
  accessToken: string
  account: AccountInfo
}
export default function useTokenClaims() {
  const { instance, accounts } = useMsal();
  const [data, setData] = useState<AuthData | null>(null);
  const location = useLocation();

  const removeQueryParams = () => {
    const hasQueryParams = location.search;
    if (hasQueryParams) {
      const newUrl = window.location.origin + location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }

  const getTokenWithClaims = async (): Promise<AuthData> => {
    if (data) return data;

    const pathName = location.pathname;

    // Validate preconditions
    if (accounts.length === 0) {
      throw new Error('No accounts available');
    }

    // Employee tools use the employee policy.  Only the legacy invite screen
    // needs an owner id to load its invitation data; a token itself does not.
    const isEmployeePath = [
      '/invite',
      '/duplicate-email-maintenance',
    ].includes(pathName);

    if (!isEmployeePath) {
      throw new Error('Unsupported path for employee token acquisition');
    }

    const authority = `${process.env.REACT_APP_MSAL_AUTHORITY}/${USER_AUTH_TYPE.COMARCH_EMPLOYEE}`;

    const silentRequest = {
      scopes: msalConfig.auth.scopes,
      account: accounts[0],
      forceRefresh: false,
      authority
    };

    try {
      const response = await instance.acquireTokenSilent(silentRequest);
      // The invite route consumes one-time owner/employee query parameters.
      // The maintenance route is standalone and must not depend on them.
      if (pathName === '/invite') removeQueryParams();

      const authData: AuthData = {
        idTokenClaims: (response.idTokenClaims || response.account?.idTokenClaims) as UserPartial,
        accessToken: response.accessToken,
        account: response.account
      };

      setData(authData);
      return authData;
    } catch (error) {
      console.error("Silent token acquisition failed:", error);

      // Force re-authentication with redirect
      const redirectRequest = {
        scopes: msalConfig.auth.scopes,
        authority,
        // Optional: preserve state to return to current page after auth
        state: JSON.stringify({
          returnUrl: window.location.pathname + window.location.search
        })
      };

      // This will redirect the user to the authentication page
      // The function will not continue after this point
      await instance.acquireTokenRedirect(redirectRequest);

      // This line will never be reached due to the redirect
      throw new Error('Redirecting for authentication');
    }
  };

  return { getTokenWithClaims };
}
